/**
 * WatchButton
 *
 * Centralized watch button component used across the entire BKFC web site.
 * Renders one of three states:
 *   - WATCH NOW  → gold CTA button (stream available / event in past / hideCountdown)
 *   - WATCH IN   → gold button with embedded countdown timer
 *   - BUY NOW    → accent button with lock icon (subscription paywall)
 *
 * Usage:
 *   import WatchButton from '../components/common/WatchButton';
 *   import { useWatchButtonState } from '../hooks/useWatchButtonState';
 *
 *   const watchState = useWatchButtonState(channel, slimEventData);
 *   <WatchButton watchState={watchState} onClick={handleClick} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import LiveBadge from '../LiveBadge';

const WatchButton = ({
  watchState,
  onClick,
  onWatchIn,
  className = '',
  size = 'md',
  showLiveBadge = true,
}) => {
  const { buttonState, countdown, isLive } = watchState;

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  // leading-none collapses each child's line-height to its font-size so the
  // play icon, the "Watch In" label, and the countdown pill all occupy boxes
  // of comparable height. items-center then aligns them at the same optical
  // axis instead of mid-line-height-padding axes.
  const baseClasses =
    'inline-flex items-center leading-none font-bold font-display uppercase tracking-wide transition-all duration-200';
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (buttonState === 'buy-now') {
    return (
      <button
        type="button"
        className={`${baseClasses} ${sizeClass} bg-accent hover:bg-accent-hover text-primary ${className}`}
        onClick={onClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4 mr-2"
        >
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
        Buy Now
      </button>
    );
  }

  if (buttonState === 'watch-in' && countdown) {
    return (
      <button
        type="button"
        className={`${baseClasses} ${sizeClass} bg-accent hover:bg-accent-hover text-primary ${className}`}
        onClick={onWatchIn || onClick}
      >
        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6 4l10 6-10 6V4z" />
        </svg>
        <span className="mr-2">Watch In</span>
        <span className="inline-flex items-center bg-black/80 text-white px-2 py-0.5 text-xs font-display tracking-wider rounded">
          {Number(countdown.days) > 0 && <>{countdown.days}:</>}
          {countdown.hours}:{countdown.minutes}:{countdown.seconds}
        </span>
      </button>
    );
  }

  // Scheduled time passed but the stream isn't live yet — no dead button.
  if (buttonState === 'starting-soon') {
    return (
      <button
        type="button"
        disabled
        className={`${baseClasses} ${sizeClass} bg-accent/60 text-primary cursor-default ${className}`}
      >
        Starting Soon…
      </button>
    );
  }

  // Default: watch-now
  return (
    <button
      type="button"
      className={`${baseClasses} ${sizeClass} bg-accent hover:bg-accent-hover text-primary ${className}`}
      onClick={onClick}
    >
      {showLiveBadge && isLive && <LiveBadge className="mr-2" />}
      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
        <path d="M6 4l10 6-10 6V4z" />
      </svg>
      Watch Now
    </button>
  );
};

WatchButton.propTypes = {
  watchState: PropTypes.shape({
    buttonState: PropTypes.oneOf([
      'watch-now',
      'watch-in',
      'buy-now',
      'starting-soon',
      'unavailable',
    ]).isRequired,
    countdown: PropTypes.shape({
      days: PropTypes.string,
      hours: PropTypes.string,
      minutes: PropTypes.string,
      seconds: PropTypes.string,
    }),
    isLive: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  onWatchIn: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showLiveBadge: PropTypes.bool,
};

export default WatchButton;
